/**
 * API Service - Smart Waste Segregation and Billing System
 *
 * Centralized data-access layer using Supabase client.
 * All methods return { data: { success, data?, error? } }.
 */

import { supabase } from '../lib/supabase';
import { emailService } from './emailService';



const wrapRpc = async (rpcPromise) => {
  const { data, error } = await rpcPromise;
  if (error) return { data: { success: false, error: error.message } };
  return { data: { success: true, data } };
};



// ---------------------------------------------------------------------------
// Auth API
// ---------------------------------------------------------------------------

export const authAPI = {
  register: async ({ name, email, password, houseNumber, estateId, contact }) => {
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: {
          name,
          role: 'resident',
          house_number: houseNumber,
          estate_id: estateId,
          contact,
        },
      },
    });
    if (error) return { data: { success: false, error: error.message } };
    return { data: { success: true, data } };
  },

  registerCollector: async ({ name, email, password, contact }) => {
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: {
          name,
          role: 'collector',
          contact,
        },
      },
    });
    if (error) return { data: { success: false, error: error.message } };
    return { data: { success: true, data } };
  },

  login: async ({ email, password }) => {
    const { data, error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) return { data: { success: false, error: error.message } };
    return { data: { success: true, data } };
  },

  logout: async () => {
    await supabase.auth.signOut();
    return { data: { success: true } };
  },

  getMe: async () => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return { data: { success: false, error: 'Not authenticated' } };

    const { data: profile, error } = await supabase
      .from('profiles')
      .select('*')
      .eq('id', user.id)
      .single();

    if (error) return { data: { success: false, error: error.message } };

    let resident = null;
    let collector = null;
    if (profile.role === 'resident') {
      const { data: r } = await supabase.from('residents').select('*').eq('user_id', user.id).maybeSingle();
      resident = r;
    }
    if (profile.role === 'collector') {
      const { data: c } = await supabase.from('collectors').select('*').eq('user_id', user.id).maybeSingle();
      collector = c;
    }

    return {
      data: {
        success: true,
        data: {
          user: {
            id: profile.id,
            name: profile.name,
            email: profile.email,
            phone: profile.phone,
            role: profile.role,
            status: profile.status,
            avatar: profile.avatar,
            resident,
            collector,
          },
        },
      },
    };
  },

  updateProfile: async (profileData) => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return { data: { success: false, error: 'Not authenticated' } };

    const updateObj = {};
    if (profileData.name) updateObj.name = profileData.name;
    if (profileData.phone) updateObj.phone = profileData.phone;
    if (profileData.avatar) updateObj.avatar = profileData.avatar;

    const { data, error } = await supabase
      .from('profiles')
      .update(updateObj)
      .eq('id', user.id)
      .select()
      .single();

    if (error) return { data: { success: false, error: error.message } };
    return { data: { success: true, data: { user: data } } };
  },

  changePassword: async ({ newPassword }) => {
    const { error } = await supabase.auth.updateUser({ password: newPassword });
    if (error) return { data: { success: false, error: error.message } };
    return { data: { success: true, message: 'Password updated successfully' } };
  },
};

// ---------------------------------------------------------------------------
// Admin API
// ---------------------------------------------------------------------------

export const adminAPI = {
  getPendingRegistrations: async () => {
    return wrapRpc(supabase.rpc('get_pending_registrations'));
  },

  approveResident: async (userId, userEmail = null, userName = null) => {
    console.log('approveResident called with:', { userId, userEmail, userName });
    const res = await wrapRpc(supabase.rpc('approve_resident', { p_user_id: userId }));
    console.log('approve_resident RPC response:', res);
    if (res.data?.success && userEmail) {
      console.log('Triggering success email to:', userEmail);
      await emailService.sendStatusEmail({ to_email: userEmail, name: userName || 'Resident', status: 'active', role: 'resident' });
    } else if (!userEmail) {
      console.warn('Cannot send approval email: userEmail is missing');
    }
    return res;
  },

  rejectResident: async (userId, reason, userEmail = null, userName = null) => {
    console.log('rejectResident called with:', { userId, reason, userEmail, userName });
    const res = await wrapRpc(supabase.rpc('reject_resident', { p_user_id: userId, p_reason: reason }));
    console.log('reject_resident RPC response:', res);
    if (res.data?.success && userEmail) {
      console.log('Triggering rejection email to:', userEmail);
      await emailService.sendStatusEmail({ to_email: userEmail, name: userName || 'Resident', status: 'rejected', role: 'resident', reason });
    } else if (!userEmail) {
      console.warn('Cannot send rejection email: userEmail is missing');
    }
    return res;
  },

  getResidents: async () => {
    const { data: residents, error } = await supabase
      .from('residents')
      .select('*, profile:profiles!user_id(id, name, email, status, role, created_at), estate:estates(id, name)') // profiles might not have user_id FK name
      .order('created_at', { ascending: false });
    if (error) return { data: { success: false, error: error.message } };
    return { data: { success: true, data: { residents: residents || [] } } };
  },

  getResidentsWithProfiles: async () => {
    const { data: profiles, error } = await supabase
      .from('profiles')
      .select('*')
      .eq('role', 'resident')
      .order('created_at', { ascending: false });
    if (error) return { data: { success: false, error: error.message } };

    const { data: residents } = await supabase.from('residents').select('*');
    const residentMap = Object.fromEntries((residents || []).map((r) => [r.user_id, r]));

    const combined = (profiles || []).map((p) => ({
      ...p,
      resident: residentMap[p.id],
    }));

    return { data: { success: true, data: { residents: combined } } };
  },

  suspendResident: async (userId) => {
    const { error } = await supabase.auth.admin.updateUserById(userId, { email_confirm: false });
    if (error) return { data: { success: false, error: error.message } };
    const { error: updateErr } = await supabase
      .from('profiles')
      .update({ status: 'suspended' })
      .eq('id', userId);
    if (updateErr) return { data: { success: false, error: updateErr.message } };
    return { data: { success: true } };
  },

  getCollectors: async () => {
    const { data: profiles, error } = await supabase
      .from('profiles')
      .select('*')
      .eq('role', 'collector')
      .order('created_at', { ascending: false });
    if (error) return { data: { success: false, error: error.message } };

    const { data: collectors } = await supabase.from('collectors').select('*');
    const collMap = Object.fromEntries((collectors || []).map((c) => [c.user_id, c]));

    const combined = (profiles || []).map((p) => ({
      ...p,
      collector: collMap[p.id],
    }));

    return { data: { success: true, data: { collectors: combined } } };
  },

  approveCollector: async (userId, estateId = null, userEmail = null, userName = null) => {
    console.log('approveCollector called with:', { userId, estateId, userEmail, userName });
    const res = await wrapRpc(supabase.rpc('approve_collector', { p_user_id: userId, p_estate_id: estateId || null }));
    console.log('approve_collector RPC response:', res);
    if (res.data?.success && userEmail) {
      console.log('Triggering success email to:', userEmail);
      await emailService.sendStatusEmail({ to_email: userEmail, name: userName || 'Collector', status: 'active', role: 'collector' });
    } else if (!userEmail) {
      console.warn('Cannot send approval email: userEmail is missing');
    }
    return res;
  },

  rejectCollector: async (userId, reason, userEmail = null, userName = null) => {
    console.log('rejectCollector called with:', { userId, reason, userEmail, userName });
    const res = await wrapRpc(supabase.rpc('reject_collector', { p_user_id: userId, p_reason: reason }));
    console.log('reject_collector RPC response:', res);
    if (res.data?.success && userEmail) {
      console.log('Triggering rejection email to:', userEmail);
      await emailService.sendStatusEmail({ to_email: userEmail, name: userName || 'Collector', status: 'rejected', role: 'collector', reason });
    } else if (!userEmail) {
      console.warn('Cannot send rejection email: userEmail is missing');
    }
    return res;
  },

  suspendCollector: async (userId) => {
    return wrapRpc(supabase.rpc('suspend_collector', { p_user_id: userId }));
  },

  assignEstateToResident: async (residentId, estateId) => {
    const { data, error } = await supabase
      .from('residents')
      .update({ estate_id: estateId })
      .eq('id', residentId)
      .select()
      .single();
    if (error) return { data: { success: false, error: error.message } };
    return { data: { success: true, data: { resident: data } } };
  },

  assignEstateToCollector: async (collectorId, estateId) => {
    const { data, error } = await supabase
      .from('collectors')
      .update({ assigned_estate_id: estateId })
      .eq('id', collectorId)
      .select()
      .single();
    if (error) return { data: { success: false, error: error.message } };
    return { data: { success: true, data: { collector: data } } };
  },

  getDashboardStats: async () => {
    return wrapRpc(supabase.rpc('get_super_admin_dashboard_stats'));
  },

  getLowConfidenceAlerts: async () => {
    return wrapRpc(supabase.rpc('get_low_confidence_alerts'));
  },

  getLastMondayCollectionStatus: async () => {
    return wrapRpc(supabase.rpc('get_last_monday_collection_status'));
  },
};

// ---------------------------------------------------------------------------
// Category API
// ---------------------------------------------------------------------------

export const categoryAPI = {
  getAll: async () => {
    const { data, error } = await supabase.from('waste_categories').select('*').order('name');
    if (error) return { data: { success: false, error: error.message } };
    return { data: { success: true, data: { categories: data || [] } } };
  },

  create: async (category) => {
    const { data, error } = await supabase
      .from('waste_categories')
      .insert({
        name: category.name,
        color_code: category.colorCode || category.color_code || '#9E9E9E',
        rate_per_kg: category.ratePerKg ?? category.rate_per_kg ?? 0,
        description: category.description || null,
      })
      .select()
      .single();
    if (error) return { data: { success: false, error: error.message } };
    return { data: { success: true, data: { category: data } } };
  },

  update: async (id, category) => {
    const updateObj = {};
    if (category.name != null) updateObj.name = category.name;
    if (category.colorCode != null || category.color_code != null) updateObj.color_code = category.colorCode || category.color_code;
    if (category.ratePerKg != null || category.rate_per_kg != null) updateObj.rate_per_kg = category.ratePerKg ?? category.rate_per_kg;
    if (category.description != null) updateObj.description = category.description;

    const { data, error } = await supabase
      .from('waste_categories')
      .update(updateObj)
      .eq('id', id)
      .select()
      .single();
    if (error) return { data: { success: false, error: error.message } };
    return { data: { success: true, data: { category: data } } };
  },
};

// ---------------------------------------------------------------------------
// Station API
// ---------------------------------------------------------------------------

export const stationAPI = {
  getAll: async () => {
    const { data, error } = await supabase
      .from('bin_stations')
      .select('*, estate:estates(id, name)')
      .order('created_at', { ascending: false });
    if (error) return { data: { success: false, error: error.message } };
    return { data: { success: true, data: { stations: data || [] } } };
  },

  create: async (station) => {
    const { data, error } = await supabase
      .from('bin_stations')
      .insert({
        estate_id: station.estateId || station.estate_id,
        location_description: station.locationDescription || station.location_description || null,
      })
      .select()
      .single();
    if (error) return { data: { success: false, error: error.message } };
    return { data: { success: true, data: { station: data } } };
  },

  getCompartments: async (stationId) => {
    const { data, error } = await supabase
      .from('bins')
      .select('*, waste_category:waste_categories(id, name, color_code)')
      .eq('station_id', stationId);
    if (error) return { data: { success: false, error: error.message } };
    return { data: { success: true, data: { compartments: data || [] } } };
  },
};

// ---------------------------------------------------------------------------
// Edge Functions API
// ---------------------------------------------------------------------------

export const edgeAPI = {
  invoke: async (functionName, body = {}) => {
    try {
      const { data, error } = await supabase.functions.invoke(functionName, {
        body,
      });
      if (error) throw error;
      return { data: { success: true, data } };
    } catch (error) {
      console.error(`Edge function error [${functionName}]:`, error);
      return { data: { success: false, error: error.message } };
    }
  },
};

// ---------------------------------------------------------------------------
// Bin API
// ---------------------------------------------------------------------------

export const binAPI = {
  getAll: async () => {
    const { data, error } = await supabase
      .from('bins')
      .select('*, waste_category:waste_categories(id, name, color_code), station:bin_stations(id, location_description)')
      .order('bin_number');
    if (error) return { data: { success: false, error: error.message } };
    return { data: { success: true, data: { bins: data || [] } } };
  },

  getByResident: async (residentId) => {
    const { data, error } = await supabase
      .from('bins')
      .select('*, waste_category:waste_categories(id, name, color_code), station:bin_stations(id, location_description)')
      .eq('resident_id', residentId)
      .order('bin_number');
    if (error) return { data: { success: false, error: error.message } };
    return { data: { success: true, data: { bins: data || [] } } };
  },

  getByStation: async (stationId) => {
    const { data, error } = await supabase
      .from('bins')
      .select('*, waste_category:waste_categories(id, name, color_code)')
      .eq('station_id', stationId)
      .order('bin_number');
    if (error) return { data: { success: false, error: error.message } };
    return { data: { success: true, data: { bins: data || [] } } };
  },

  getAvailableByCategory: async (wasteCategoryId, residentId) => {
    let query = supabase
      .from('bins')
      .select('*, waste_category:waste_categories(id, name, color_code)')
      .eq('waste_category_id', wasteCategoryId)
      .eq('is_full', false);
    if (residentId) query = query.eq('resident_id', residentId);
    query = query.order('bin_number', { ascending: true }).limit(1);
    const { data, error } = await query;
    if (error) return { data: { success: false, error: error.message } };
    return { data: { success: true, data: { bin: data?.[0] || null } } };
  },

  updateFillLevel: async (binId, newFillLevel) => {
    const isFull = newFillLevel >= 90;
    const cappedLevel = Math.min(newFillLevel, 100);
    const { data, error } = await supabase
      .from('bins')
      .update({ fill_level: cappedLevel, is_full: isFull })
      .eq('id', binId)
      .select()
      .single();
    if (error) return { data: { success: false, error: error.message } };
    return { data: { success: true, data: { bin: data } } };
  },

  getFullBins: async () => {
    const { data, error } = await supabase
      .from('bins')
      .select('*, waste_category:waste_categories(id, name, color_code), station:bin_stations(id, location_description, estate_id)')
      .eq('is_full', true)
      .order('updated_at', { ascending: false });
    if (error) return { data: { success: false, error: error.message } };
    return { data: { success: true, data: { bins: data || [] } } };
  },
};

// ---------------------------------------------------------------------------
// Bin Commands API
// ---------------------------------------------------------------------------

export const binCommandAPI = {
  send: async (command) => {
    console.log('Sending Bin Command:', command);
    const { data, error } = await supabase
      .from('bin_commands')
      .insert({
        waste_type: command.waste_type,
        target_bin: command.target_bin,
        status: 'pending',
        resident_id: command.resident_id,
      })
      .select()
      .single();
    if (error) {
      // Handle "PGRST204" - Column not found in cache (common after migrations)
      if (error.code === 'PGRST204') {
        return {
          data: {
            success: false,
            error: 'Database schema cache is stale. Please run "NOTIFY pgrst, \'reload schema\';" in your Supabase SQL editor.'
          }
        };
      }
      return { data: { success: false, error: error.message } };
    }
    return { data: { success: true, data: { command: data } } };
  },

  subscribe: (commandId, callback) => {
    return supabase
      .channel(`bin_command_${commandId}`)
      .on('postgres_changes', {
        event: 'UPDATE',
        schema: 'public',
        table: 'bin_commands',
        filter: `id=eq.${commandId}`,
      }, (payload) => {
        callback(payload.new);
      })
      .subscribe();
  },

  unsubscribe: (channel) => {
    if (channel) supabase.removeChannel(channel);
  },
};

// ---------------------------------------------------------------------------
// Wrong Bin Attempts API
// ---------------------------------------------------------------------------

export const wrongBinAPI = {
  log: async (attempt) => {
    const { data, error } = await supabase
      .from('wrong_bin_attempts')
      .insert({
        resident_id: attempt.resident_id,
        bin_id: attempt.bin_id,
        expected_waste: attempt.expected_waste,
        attempted_waste: attempt.attempted_waste,
      })
      .select()
      .single();
    if (error) return { data: { success: false, error: error.message } };
    return { data: { success: true, data: { attempt: data } } };
  },

  getRecent: async (limit = 20) => {
    const { data, error } = await supabase
      .from('wrong_bin_attempts')
      .select('*, bin:bins(id, bin_name, waste_category:waste_categories(name)), resident:residents(id, profile:profiles!user_id(name))')
      .order('timestamp', { ascending: false })
      .limit(limit);
    if (error) return { data: { success: false, error: error.message } };
    return { data: { success: true, data: { attempts: data || [] } } };
  },
};

// ---------------------------------------------------------------------------
// Estate API
// ---------------------------------------------------------------------------

export const estateAPI = {
  getAll: async () => {
    const { data, error } = await supabase.from('estates').select('*').order('name');
    if (error) return { data: { success: false, error: error.message } };
    return { data: { success: true, data: { estates: data || [] } } };
  },

  create: async (estate) => {
    const { data, error } = await supabase
      .from('estates')
      .insert({
        name: estate.name?.trim() || '',
        location: estate.location?.trim() || null,
      })
      .select()
      .single();
    if (error) return { data: { success: false, error: error.message } };
    return { data: { success: true, data: { estate: data } } };
  },

  update: async (id, estate) => {
    const updateObj = {};
    if (estate.name != null) updateObj.name = estate.name.trim();
    if (estate.location != null) updateObj.location = estate.location?.trim() || null;
    const { data, error } = await supabase
      .from('estates')
      .update(updateObj)
      .eq('id', id)
      .select()
      .single();
    if (error) return { data: { success: false, error: error.message } };
    return { data: { success: true, data: { estate: data } } };
  },

  delete: async (id) => {
    const { error } = await supabase.from('estates').delete().eq('id', id);
    if (error) return { data: { success: false, error: error.message } };
    return { data: { success: true } };
  },
};

// ---------------------------------------------------------------------------
// Disposal API
// ---------------------------------------------------------------------------

export const disposalAPI = {
  scan: async (formData) => {
    const baseUrl = process.env.REACT_APP_SUPABASE_URL || 'https://bvlhpsghcddxlltecezw.supabase.co';
    const url = `${baseUrl}/functions/v1/disposal-scan`;
    const { data: { session } } = await supabase.auth.getSession();
    const token = session?.access_token;

    const res = await fetch(url, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        ...(formData instanceof FormData ? {} : { 'Content-Type': 'application/json' }),
      },
      body: formData instanceof FormData ? formData : JSON.stringify(formData),
    });

    const json = await res.json().catch(() => ({}));
    if (!res.ok) {
      return { data: { success: false, error: json.error || res.statusText } };
    }
    return { data: { success: true, data: json } };
  },

  getLogs: async (params = {}) => {
    let query = supabase
      .from('disposal_logs')
      .select('*, resident:residents(id, house_number, profile:profiles!user_id(name)), waste_category:waste_categories(id, name, color_code), station:bin_stations(id, location_description)')
      .order('timestamp', { ascending: false });

    if (params.residentId) query = query.eq('resident_id', params.residentId);
    if (params.limit) query = query.limit(params.limit);

    const { data, error } = await query;
    if (error) return { data: { success: false, error: error.message } };
    return { data: { success: true, data: { logs: data || [] } } };
  },

  getLogsByResident: async (residentId) => {
    return disposalAPI.getLogs({ residentId });
  },

  getCategoryBreakdown: async () => {
    return wrapRpc(supabase.rpc('get_waste_category_breakdown'));
  },
};

// ---------------------------------------------------------------------------
// Schedule API
// ---------------------------------------------------------------------------

export const scheduleAPI = {
  getSchedule: async () => {
    return wrapRpc(supabase.rpc('get_schedule_for_monday'));
  },

  updateNextDate: async (scheduleId, nextDate) => {
    const { data, error } = await supabase
      .from('collection_schedules')
      .update({ next_collection_date: nextDate })
      .eq('id', scheduleId)
      .select()
      .single();
    if (error) return { data: { success: false, error: error.message } };
    return { data: { success: true, data: { schedule: data } } };
  },
};

// ---------------------------------------------------------------------------
// Collection API
// ---------------------------------------------------------------------------

export const collectionAPI = {
  logEvent: async (event) => {
    try {
      const { data: { user }, error: authErr } = await supabase.auth.getUser();
      if (authErr || !user) {
        console.error('logEvent: auth error', authErr);
        return { data: { success: false, error: 'Not authenticated. Please log in again.' } };
      }

      const { data: collector, error: colErr } = await supabase.from('collectors').select('id').eq('user_id', user.id).single();
      if (colErr || !collector) {
        console.error('logEvent: collector lookup failed', colErr, 'user_id:', user.id);
        return { data: { success: false, error: 'Collector profile not found. Please contact the administrator.' } };
      }

      console.log('logEvent: inserting collection event', { schedule_id: event.scheduleId || event.schedule_id, collector_id: collector.id, date: event.date, status: event.status });

      const { data, error } = await supabase
        .from('collection_events')
        .insert({
          schedule_id: event.scheduleId || event.schedule_id,
          collector_id: collector.id,
          date: event.date,
          status: event.status || 'completed',
          notes: event.notes || null,
        })
        .select()
        .single();
      if (error) {
        console.error('logEvent: insert error', error);
        return { data: { success: false, error: error.message } };
      }
      return { data: { success: true, data: { event: data } } };
    } catch (err) {
      console.error('logEvent: unexpected error', err);
      return { data: { success: false, error: err.message || 'Unexpected error logging collection event.' } };
    }
  },

  getEvents: async (params = {}) => {
    let query = supabase
      .from('collection_events')
      .select('*, schedule:collection_schedules(*, waste_category:waste_categories(id, name, color_code)), collector:collectors(id)')
      .order('date', { ascending: false });

    if (params.date) query = query.eq('date', params.date);

    const { data, error } = await query;
    if (error) return { data: { success: false, error: error.message } };
    return { data: { success: true, data: { events: data || [] } } };
  },
};

// ---------------------------------------------------------------------------
// Bill API
// ---------------------------------------------------------------------------

export const billAPI = {
  generate: async (month, year) => {
    const { data: { session } } = await supabase.auth.getSession();
    return edgeAPI.invoke('generate-bills', { month, year, generatedBy: session?.user?.id });
  },

  getByResident: async (residentId) => {
    const { data, error } = await supabase
      .from('bills')
      .select('*, line_items:bill_line_items(*, waste_category:waste_categories(id, name, color_code))')
      .eq('resident_id', residentId)
      .order('billing_period_year', { ascending: false })
      .order('billing_period_month', { ascending: false });
    if (error) return { data: { success: false, error: error.message } };
    return { data: { success: true, data: { bills: data || [] } } };
  },

  pay: async (billId, amount, method = 'portal') => {
    const { data: { user } } = await supabase.auth.getUser();
    const { data: resident } = await supabase.from('residents').select('id').eq('user_id', user?.id).single();
    const { data: bill } = await supabase.from('bills').select('resident_id').eq('id', billId).single();
    if (bill?.resident_id !== resident?.id) return { data: { success: false, error: 'Unauthorized' } };

    const { error: payErr } = await supabase.from('payments').insert({
      bill_id: billId,
      amount: amount,
      method,
      confirmed_by: null,
    });
    if (payErr) return { data: { success: false, error: payErr.message } };
    return { data: { success: true, message: 'Payment recorded. Awaiting admin confirmation.' } };
  },

  confirmPayment: async (billId) => {
    return wrapRpc(supabase.rpc('confirm_payment', { p_bill_id: billId }));
  },

  /** Initiate M-Pesa STK Push for a bill */
  initiateMpesa: async (billId, phoneNumber) => {
    return edgeAPI.invoke('mpesa-stk-push', { billId, phoneNumber });
  },

  /** Check M-Pesa payment status by CheckoutRequestID */
  checkPaymentStatus: async (checkoutRequestId) => {
    const { data, error } = await supabase
      .from('payments')
      .select('id, bill_id, amount, payment_status, mpesa_receipt_number, phone_number, transaction_date')
      .eq('mpesa_checkout_request_id', checkoutRequestId)
      .maybeSingle();
    if (error) return { data: { success: false, error: error.message } };
    return { data: { success: true, data: { payment: data } } };
  },

  getAll: async (params = {}) => {
    let query = supabase
      .from('bills')
      .select('*, resident:residents(id, house_number, profile:profiles!user_id(name, email)), line_items:bill_line_items(*, waste_category:waste_categories(id, name, color_code)), payments(id, mpesa_receipt_number, payment_status, method)')
      .order('generated_date', { ascending: false });

    if (params.status) query = query.eq('status', params.status);
    if (params.month) query = query.eq('billing_period_month', params.month);
    if (params.year) query = query.eq('billing_period_year', params.year);

    const { data, error } = await query;
    if (error) return { data: { success: false, error: error.message } };
    return { data: { success: true, data: { bills: data || [] } } };
  },
};

// ---------------------------------------------------------------------------
// Notification API
// ---------------------------------------------------------------------------

export const notificationAPI = {
  getByUser: async (userId) => {
    const { data, error } = await supabase
      .from('notifications')
      .select('*')
      .eq('user_id', userId)
      .order('created_at', { ascending: false })
      .limit(50);
    if (error) return { data: { success: false, error: error.message } };
    return { data: { success: true, data: { notifications: data || [] } } };
  },

  markRead: async (id) => {
    const { error } = await supabase
      .from('notifications')
      .update({ is_read: true, read_at: new Date().toISOString() })
      .eq('id', id);
    if (error) return { data: { success: false, error: error.message } };
    return { data: { success: true } };
  },

  markAllAsRead: async (userId) => {
    const { data: { user } } = await supabase.auth.getUser();
    const uid = userId || user?.id;
    if (!uid) return { data: { success: false, error: 'Not authenticated' } };
    const { error } = await supabase
      .from('notifications')
      .update({ is_read: true, read_at: new Date().toISOString() })
      .eq('user_id', uid)
      .eq('is_read', false);
    if (error) return { data: { success: false, error: error.message } };
    return { data: { success: true } };
  },
};// ---------------------------------------------------------------------------
// Support API
// ---------------------------------------------------------------------------

export const supportAPI = {
  create: async (ticket) => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return { data: { success: false, error: 'Not authenticated' } };

    const { data, error } = await supabase
      .from('support_tickets')
      .insert({
        user_id: user.id,
        subject: ticket.subject,
        category: ticket.category,
        priority: ticket.priority,
        description: ticket.description,
      })
      .select()
      .single();
    if (error) return { data: { success: false, error: error.message } };
    return { data: { success: true, data: { ticket: data } } };
  },

  getByUser: async () => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return { data: { success: false, error: 'Not authenticated' } };

    const { data, error } = await supabase
      .from('support_tickets')
      .select('*')
      .eq('user_id', user.id)
      .order('created_at', { ascending: false });
    if (error) return { data: { success: false, error: error.message } };
    return { data: { success: true, data: { tickets: data || [] } } };
  },

  getAll: async () => {
    const { data, error } = await supabase
      .from('support_tickets')
      .select('*, profile:profiles!user_id(name, email, role)')
      .order('created_at', { ascending: false });
    if (error) return { data: { success: false, error: error.message } };
    return { data: { success: true, data: { tickets: data || [] } } };
  },

  getById: async (ticketId) => {
    const { data, error } = await supabase
      .from('support_tickets')
      .select('*, profile:profiles!user_id(name, email, role), replies:ticket_replies(*, profile:profiles!user_id(name, role))')
      .eq('id', ticketId)
      .order('created_at', { referencedTable: 'ticket_replies', ascending: true })
      .single();
    if (error) return { data: { success: false, error: error.message } };
    return { data: { success: true, data: { ticket: data } } };
  },

  updateStatus: async (ticketId, status) => {
    const { data, error } = await supabase
      .from('support_tickets')
      .update({ status })
      .eq('id', ticketId)
      .select()
      .single();
    if (error) return { data: { success: false, error: error.message } };
    return { data: { success: true, data: { ticket: data } } };
  },

  addReply: async (ticketId, message) => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return { data: { success: false, error: 'Not authenticated' } };

    const { data, error } = await supabase
      .from('ticket_replies')
      .insert({
        ticket_id: ticketId,
        user_id: user.id,
        message,
      })
      .select('*, profile:profiles!user_id(name, role)')
      .single();
    if (error) return { data: { success: false, error: error.message } };
    return { data: { success: true, data: { reply: data } } };
  },
};

export default supabase;
